import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
};

export const AppProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/settings"),
        api.get("/content"),
      ]);
      setSettings(s.data);
      setContent(c.data);
      // Apply design system to CSS variables
      const ds = s.data?.design_system || {};
      const root = document.documentElement;
      const cssVars = {
        "--color-primary": ds.color_primary,
        "--color-secondary": ds.color_secondary,
        "--color-bg-primary": ds.color_bg_primary,
        "--color-bg-dark": ds.color_bg_dark,
        "--color-text-primary": ds.color_text_primary,
        "--color-text-secondary": ds.color_text_secondary,
        "--color-gold": ds.color_gold,
        "--color-success": ds.color_success,
        "--color-warning": ds.color_warning,
        "--color-danger": ds.color_danger,
        "--color-border": ds.color_border,
      };
      Object.entries(cssVars).forEach(([k, v]) => { if (v) root.style.setProperty(k, v); });
    } catch (e) {
      console.error("Failed to load app settings/content", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <AppContext.Provider value={{ settings, content, loading, reload }}>
      {children}
    </AppContext.Provider>
  );
};

// Helper to read content with fallback
export const t = (content, path, fallback = "") => {
  if (!content) return fallback;
  const parts = path.split(".");
  let cur = content;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return fallback;
  }
  return cur ?? fallback;
};
